/**
 * Companies List Page
 *
 * Displays a list of companies with filtering, search, and pagination.
 */

import { Building2, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ContactTypeSelectorDialog } from '../components/ContactTypeSelectorDialog';
import { useCompanies, useCompanyCategories } from '../hooks';
import type { CompanyGroupName, CompanyStatus, ContactType } from '../types';

const STATUS_OPTIONS: { value: CompanyStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const GROUP_OPTIONS: { value: CompanyGroupName | 'all'; label: string }[] = [
  { value: 'all', label: 'All Groups' },
  { value: 'Closing', label: 'Closing' },
  { value: 'Payoffs', label: 'Payoffs' },
  { value: 'Services', label: 'Services' },
  { value: 'Government', label: 'Government' },
  { value: 'Other', label: 'Other' },
];

function getStatusBadgeVariant(status: CompanyStatus): 'default' | 'secondary' {
  return status === 'active' ? 'default' : 'secondary';
}

function getGroupBadgeClass(groupName: CompanyGroupName): string {
  const classes: Record<CompanyGroupName, string> = {
    Closing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
    Payoffs: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300',
    Services: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
    Government: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
    Other: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  };
  return classes[groupName] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
}

export default function CompaniesListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [contactType, setContactType] = useState<ContactType>('organization');
  const [status, setStatus] = useState<CompanyStatus | 'all'>('all');
  const [groupName, setGroupName] = useState<CompanyGroupName | 'all'>('all');
  const [categoryId, setCategoryId] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const limit = 20;

  const { data: categoriesData } = useCompanyCategories();

  const { data, isLoading, error } = useCompanies({
    contact_type: contactType,
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    group_name: groupName === 'all' ? undefined : groupName,
    category_id: categoryId === 'all' ? undefined : categoryId,
    page,
    limit,
  });

  // Filter categories by selected group
  const filteredCategories =
    groupName === 'all'
      ? categoriesData
      : categoriesData?.filter((c) => c.group_name === groupName);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleContactTypeChange = (value: ContactType) => {
    setContactType(value);
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value as CompanyStatus | 'all');
    setPage(1);
  };

  const handleGroupChange = (value: string) => {
    setGroupName(value as CompanyGroupName | 'all');
    setCategoryId('all'); // Reset category when group changes
    setPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setCategoryId(value);
    setPage(1);
  };

  const handleRowClick = (companyId: string) => {
    navigate(`/contacts/${companyId}`);
  };

  const handleCreateCompany = () => {
    setIsDialogOpen(true);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-600">Error loading companies: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Contacts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage companies, lenders, and service providers
          </p>
        </div>
        <Button onClick={handleCreateCompany}>
          <Plus className="h-4 w-4 mr-2" />
          Add Contact
        </Button>
      </div>

      {/* Contact Type Tabs */}
      <div className="flex items-center justify-between">
        <Tabs
          value={contactType}
          onValueChange={(value) => handleContactTypeChange(value as ContactType)}
        >
          <TabsList>
            <TabsTrigger value="organization">Organizations</TabsTrigger>
            <TabsTrigger value="individual">Individuals</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={`Search by ${contactType === 'individual' ? 'name' : 'company name'}...`}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {contactType === 'organization' && (
          <>
            <Select value={groupName} onValueChange={handleGroupChange}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by group" />
              </SelectTrigger>
              <SelectContent>
                {GROUP_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={categoryId} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {filteredCategories?.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}

        <Select value={status} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded-lg bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              {contactType === 'organization' ? (
                <>
                  <TableHead>Company</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                </>
              ) : (
                <>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Loading companies...
                </TableCell>
              </TableRow>
            ) : !data?.companies?.length ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                    <div className="text-muted-foreground">
                      {search ||
                      status !== 'all' ||
                      (contactType === 'organization' &&
                        (groupName !== 'all' || categoryId !== 'all'))
                        ? `No ${contactType === 'individual' ? 'individuals' : 'companies'} match your filters`
                        : `No ${contactType === 'individual' ? 'individuals' : 'companies'} yet`}
                    </div>
                    {!search &&
                      status === 'all' &&
                      (contactType === 'individual' ||
                        (groupName === 'all' && categoryId === 'all')) && (
                        <Button variant="link" onClick={handleCreateCompany} className="mt-2">
                          Add your first{' '}
                          {contactType === 'individual' ? 'individual' : 'organization'}
                        </Button>
                      )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.companies.map((company) => (
                <TableRow
                  key={company.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(company.id)}
                >
                  {contactType === 'organization' ? (
                    <>
                      <TableCell>
                        <div>
                          <div className="font-medium">{company.name}</div>
                          {company.website && (
                            <div className="text-sm text-muted-foreground">{company.website}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {company.category && (
                          <div className="flex flex-col gap-1">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getGroupBadgeClass(company.category.group_name)} w-fit`}
                            >
                              {company.category.group_name}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {company.category.name}
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {company.primary_contact_name && (
                            <div>{company.primary_contact_name}</div>
                          )}
                          {company.phone && (
                            <div className="text-muted-foreground">{company.phone}</div>
                          )}
                          {company.primary_contact_email && (
                            <div className="text-muted-foreground">
                              {company.primary_contact_email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {company.address_city || company.address_state ? (
                          <div className="text-sm text-muted-foreground">
                            {[company.address_city, company.address_state]
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(company.status)}>
                          {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
                        </Badge>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        <div className="font-medium">
                          {company.primary_contact_name || company.name || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{company.primary_contact_email || '-'}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{company.primary_contact_title || '-'}</div>
                      </TableCell>
                      <TableCell>
                        {company.address_city || company.address_state ? (
                          <div className="text-sm text-muted-foreground">
                            {[company.address_city, company.address_state]
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(company.status)}>
                          {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
                        </Badge>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, data.pagination.total)} of{' '}
            {data.pagination.total} companies
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {data.pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
              disabled={page === data.pagination.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Contact Type Selector Dialog */}
      <ContactTypeSelectorDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </div>
  );
}
